import { useState, useEffect } from "react";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { Bell, AlertTriangle, Clock, Users, Package } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getDaysLeft(endDate) {
    if (!endDate) return null;
    return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
}

function AlertCard({ icon: Icon, title, count, color, children }) {
    const colors = {
        red: "border-l-rose-500 bg-rose-50/50",
        amber: "border-l-amber-500 bg-amber-50/50",
        blue: "border-l-blue-500 bg-blue-50/50",
        slate: "border-l-slate-400 bg-slate-50",
    };
    const iconColors = {
        red: "text-rose-500", amber: "text-amber-500", blue: "text-blue-500", slate: "text-slate-400"
    };
    return (
        <div className={`bg-white border border-slate-200 border-l-4 ${colors[color]} rounded`}>
            <div className="px-5 py-4 flex items-center justify-between border-b border-slate-200/80">
                <div className="flex items-center gap-2.5">
                    <Icon size={18} className={iconColors[color]} />
                    <span className="font-semibold text-slate-900 text-sm">{title}</span>
                </div>
                <span className={`text-lg font-bold font-mono ${count > 0 ? iconColors[color] : "text-slate-300"}`}>{count}</span>
            </div>
            <div className="divide-y divide-slate-100">
                {children}
            </div>
            {count === 0 && (
                <div className="px-5 py-4 text-sm text-slate-400 text-center">Asnjë alert aktiv</div>
            )}
        </div>
    );
}

export default function AlertsPage() {
    const { t } = useLang();
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAlerts();
    }, []);

    const fetchAlerts = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/alerts`, { withCredentials: true });
            setAlerts(data);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const totalAlerts = alerts
        ? (alerts.counts?.expiring || 0) + (alerts.counts?.expired || 0) + (alerts.counts?.debtors || 0) + (alerts.counts?.stock || 0)
        : 0;

    if (loading) {
        return <div className="p-6 text-slate-400">{t("loading")}</div>;
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <Bell size={20} className="text-[#E7212E]" /> {t("alerts")}
                    {totalAlerts > 0 && (
                        <span className="bg-[#E7212E] text-white text-xs px-2 py-0.5 rounded-full font-medium ml-1">{totalAlerts}</span>
                    )}
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                    {totalAlerts > 0 ? `${totalAlerts} ${t("alert_count")}` : "Asnjë problem aktiv"}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Expired Subscriptions */}
                <AlertCard
                    icon={AlertTriangle}
                    title={t("expired_subscriptions")}
                    count={alerts?.counts?.expired || 0}
                    color="red"
                >
                    {alerts?.expired_subscriptions?.map((s, i) => {
                        const days = getDaysLeft(s.end_date);
                        return (
                            <div key={s.id || i} className="px-5 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => navigate("/subscriptions")} data-testid={`expired-sub-${s.id}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-mono text-sm font-medium text-slate-900">{s.imei}</p>
                                        <p className="text-xs text-slate-500">{s.client_name} · Skadoi: {s.end_date?.substring(0, 10)}</p>
                                    </div>
                                    <span className="text-xs font-medium text-rose-600 bg-rose-50 px-2 py-0.5 rounded">
                                        {Math.abs(days || 0)}d vonë
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </AlertCard>

                {/* Expiring Soon */}
                <AlertCard
                    icon={Clock}
                    title={t("expiring_subscriptions")}
                    count={alerts?.counts?.expiring || 0}
                    color="amber"
                >
                    {alerts?.expiring_subscriptions?.map((s, i) => {
                        const days = getDaysLeft(s.end_date);
                        return (
                            <div key={s.id || i} className="px-5 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => navigate("/subscriptions")} data-testid={`expiring-sub-${s.id}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-mono text-sm font-medium text-slate-900">{s.imei}</p>
                                        <p className="text-xs text-slate-500">{s.client_name} · Skadon: {s.end_date?.substring(0, 10)}</p>
                                    </div>
                                    <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                                        {days} {t("days_left")}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </AlertCard>

                {/* Debtor Clients */}
                <AlertCard
                    icon={Users}
                    title={t("debtor_clients")}
                    count={alerts?.counts?.debtors || 0}
                    color="red"
                >
                    {alerts?.debtor_clients?.map((c, i) => (
                        <div key={c.id || i} className="px-5 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => navigate("/clients")} data-testid={`debtor-client-${c.id}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-900">{c.name}</p>
                                    <p className="text-xs text-slate-500">{c.nipt || ""} · {c.phone}</p>
                                </div>
                                <span className="text-xs font-medium text-rose-600 bg-rose-50 px-2 py-0.5 rounded">
                                    {t("debtor")}
                                </span>
                            </div>
                        </div>
                    ))}
                </AlertCard>

                {/* Stock Devices */}
                <AlertCard
                    icon={Package}
                    title={t("stock_devices")}
                    count={alerts?.counts?.stock || 0}
                    color="blue"
                >
                    {alerts?.stock_devices?.map((d, i) => (
                        <div key={d.id || i} className="px-5 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => navigate("/devices")} data-testid={`stock-device-${d.id}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-mono text-sm font-medium text-slate-900">{d.imei}</p>
                                    <p className="text-xs text-slate-500">{d.model || "—"}</p>
                                </div>
                                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                    {t("stock")}
                                </span>
                            </div>
                        </div>
                    ))}
                </AlertCard>
            </div>

            {totalAlerts === 0 && (
                <div className="mt-8 text-center py-16 bg-white border border-slate-200 rounded">
                    <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Bell size={22} className="text-emerald-500" />
                    </div>
                    <p className="text-slate-900 font-medium">Gjithçka është në rregull!</p>
                    <p className="text-sm text-slate-500 mt-1">Nuk ka alarme aktive për momentin.</p>
                </div>
            )}
        </div>
    );
}
